/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Wizard actions
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.10.0
 * @since 1.2.0
 */

import { ILogoutStartSuccessful } from '../../pages/PageAuth/types/authTypes';
import { createAction } from '../app/createAction';
import {
    EWizardName,
    WIZARD_BACK_ACTION,
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_CANCEL_ACTION,
    WIZARD_FINISH_ACTION,
    WIZARD_INIT_CURRENT_SAGA,
    WIZARD_LOADER_START,
    WIZARD_LOADER_STOP,
    WIZARD_META_ACTION,
    WIZARD_META_FAILED,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_ACTION,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_OPEN_MODAL_SUMMARY,
    WIZARD_REPORT_SET_ERRORS,
    WIZARD_REPORT_SET_PARAM,
    WIZARD_REPORT_SET_PARAMS,
    WIZARD_REPORT_STORAGE_SAVE,
    WIZARD_RESTORE_ACTION,
    WIZARD_RESTORE_FAILED,
    WIZARD_RESTORE_SUCCESS,
    WIZARD_SET_ENTITY_ID,
    WIZARD_SET_IS_REQUESTS_STICKY,
    WIZARD_START_ACTION,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
    WIZARD_STORAGE_CLEAR_STEPS,
    WIZARD_STORAGE_CLEAR_STEPS_SUCCESS,
    WIZARD_STORAGE_LOAD_STEPS,
    WIZARD_STORAGE_SAVE_STEP,
    WIZARD_STORAGE_UPDATE_STEP,
} from './wizardConstants';
import {
    IWizardBackSuccessPayload,
    IWizardMeta,
    IWizardNextActionPayload,
    IWizardNextSuccessPayload,
    IWizardOpenModalSummaryPayload,
    IWizardReportSetErrorsPayload,
    IWizardReportSetParamPayload,
    IWizardReportSetParamsPayload,
    IWizardRestoreFailedPayload,
    IWizardRestorePayload,
    IWizardRestoreSuccessPayload,
    IWizardStartFailedPayload,
    IWizardStartPayload,
    IWizardStartSuccessPayload,
    IWizardStorageClearStepsPayload,
    IWizardStorageSaveStepPayload,
    IWizardStorageUpdateStepPayload,
    TWizardBackAction,
    TWizardBackFailedAction,
    TWizardBackSuccessAction,
    TWizardCancelAction,
    TWizardFinishAction,
    TWizardLoaderStartAction,
    TWizardLoaderStopAction,
    TWizardMetaAction,
    TWizardMetaFailedAction,
    TWizardMetaSuccessAction,
    TWizardNextAction,
    TWizardNextFailedAction,
    TWizardNextSuccessAction,
    TWizardOpenModalSummaryAction,
    TWizardReportSetErrorsAction,
    TWizardReportSetParamAction,
    TWizardReportSetParamsAction,
    TWizardRestoreAction,
    TWizardRestoreFailedAction,
    TWizardRestoreSuccessAction,
    TWizardSetEntityIdAction,
    TWizardSetIsRequestsStickyAction,
    TWizardStartAction,
    TWizardStartFailedAction,
    TWizardStartSuccessAction,
    TWizardStorageClearStepsAction,
    TWizardStorageClearStepsSuccess,
    TWizardStorageLoadStepsAction,
    TWizardStorageSaveStepAction,
    TWizardStorageUpdateStepAction,
} from './wizardTypes';

export const wizardStartAction = (payload?: IWizardStartPayload): TWizardStartAction =>
    createAction(WIZARD_START_ACTION, payload);
export const wizardStartSuccess = (payload: IWizardStartSuccessPayload): TWizardStartSuccessAction =>
    createAction(WIZARD_START_SUCCESS, payload);
export const wizardStartFailed = (payload: IWizardStartFailedPayload): TWizardStartFailedAction =>
    createAction(WIZARD_START_FAILED, payload);

export const wizardRestoreAction = (payload: IWizardRestorePayload): TWizardRestoreAction =>
    createAction(WIZARD_RESTORE_ACTION, payload);
export const wizardRestoreSuccess = (payload: IWizardRestoreSuccessPayload): TWizardRestoreSuccessAction =>
    createAction(WIZARD_RESTORE_SUCCESS, payload);
export const wizardRestoreFailed = (payload: IWizardRestoreFailedPayload): TWizardRestoreFailedAction =>
    createAction(WIZARD_RESTORE_FAILED, payload);

export const wizardNextAction = (payload: IWizardNextActionPayload): TWizardNextAction =>
    createAction(WIZARD_NEXT_ACTION, payload);
export const wizardNextSuccess = (payload: IWizardNextSuccessPayload): TWizardNextSuccessAction =>
    createAction(WIZARD_NEXT_SUCCESS, payload);
export const wizardNextFailed = (payload: any): TWizardNextFailedAction =>
    createAction(WIZARD_NEXT_FAILED, payload);

export const wizardBackAction = (): TWizardBackAction =>
    createAction(WIZARD_BACK_ACTION);
export const wizardBackSuccess = (payload: IWizardBackSuccessPayload): TWizardBackSuccessAction =>
    createAction(WIZARD_BACK_SUCCESS, payload);
export const wizardBackFailed = (payload: any): TWizardBackFailedAction =>
    createAction(WIZARD_BACK_FAILED, payload);

export const wizardMetaAction = (): TWizardMetaAction =>
    createAction(WIZARD_META_ACTION);
export const wizardMetaSuccess = (payload: IWizardMeta): TWizardMetaSuccessAction =>
    createAction(WIZARD_META_SUCCESS, payload);
export const wizardMetaFailed = (payload: any): TWizardMetaFailedAction =>
    createAction(WIZARD_META_FAILED, payload);

export const wizardCancelAction = (): TWizardCancelAction =>
    createAction(WIZARD_CANCEL_ACTION);

export const wizardFinishAction = (): TWizardFinishAction =>
    createAction(WIZARD_FINISH_ACTION);

export const wizardStorageClearStepsAction =
    (payload: IWizardStorageClearStepsPayload): TWizardStorageClearStepsAction =>
        createAction(WIZARD_STORAGE_CLEAR_STEPS, payload);
export const wizardStorageClearStepsSuccess = (): TWizardStorageClearStepsSuccess =>
    createAction(WIZARD_STORAGE_CLEAR_STEPS_SUCCESS);

export const wizardStorageLoadStepsAction = (): TWizardStorageLoadStepsAction =>
    createAction(WIZARD_STORAGE_LOAD_STEPS);
export const wizardStorageSaveStepAction = (payload: IWizardStorageSaveStepPayload): TWizardStorageSaveStepAction =>
    createAction(WIZARD_STORAGE_SAVE_STEP, payload);
export const wizardStorageUpdateStepAction = (payload: IWizardStorageUpdateStepPayload):
    TWizardStorageUpdateStepAction => createAction(WIZARD_STORAGE_UPDATE_STEP, payload);

export const wizardLoaderStartAction = (): TWizardLoaderStartAction =>
    createAction(WIZARD_LOADER_START);
export const wizardLoaderStopAction = (): TWizardLoaderStopAction =>
    createAction(WIZARD_LOADER_STOP);

export const wizardInitCurrentSagaAction = (payload: EWizardName) => createAction(WIZARD_INIT_CURRENT_SAGA, payload);

export const wizardReportSetParamAction =
    (payload: IWizardReportSetParamPayload): TWizardReportSetParamAction =>
        createAction(WIZARD_REPORT_SET_PARAM, payload);

export const wizardReportSetParamsAction =
    (payload: IWizardReportSetParamsPayload): TWizardReportSetParamsAction =>
        createAction(WIZARD_REPORT_SET_PARAMS, payload);

export const wizardReportSetErrorsAction =
    (payload: IWizardReportSetErrorsPayload): TWizardReportSetErrorsAction =>
        createAction(WIZARD_REPORT_SET_ERRORS, payload);

export const wizardReportStorageSaveAction = () => createAction(WIZARD_REPORT_STORAGE_SAVE);


export const wizardSetEntityIdAction = (payload: number): TWizardSetEntityIdAction =>
    createAction(WIZARD_SET_ENTITY_ID, payload);

export const wizardOpenModalSummaryAction = (payload: IWizardOpenModalSummaryPayload): TWizardOpenModalSummaryAction =>
    createAction(WIZARD_OPEN_MODAL_SUMMARY, payload);

export const wizardSetIsRequestsSticky = (payload: boolean): TWizardSetIsRequestsStickyAction =>
    createAction(WIZARD_SET_IS_REQUESTS_STICKY, payload);

export type TWizardActions =
    TWizardStartAction
    | TWizardStartSuccessAction
    | TWizardStartFailedAction
    | TWizardRestoreAction
    | TWizardRestoreSuccessAction
    | TWizardRestoreFailedAction
    | TWizardNextAction
    | TWizardNextSuccessAction
    | TWizardNextFailedAction
    | TWizardBackAction
    | TWizardBackSuccessAction
    | TWizardBackFailedAction
    | TWizardMetaAction
    | TWizardMetaSuccessAction
    | TWizardMetaFailedAction
    | TWizardFinishAction
    | TWizardStorageLoadStepsAction
    | TWizardStorageSaveStepAction
    | TWizardLoaderStartAction
    | TWizardLoaderStopAction
    | TWizardReportSetParamAction
    | TWizardReportSetParamsAction
    | TWizardSetEntityIdAction
    | TWizardSetIsRequestsStickyAction
    | TWizardOpenModalSummaryAction
    | TWizardReportSetErrorsAction
    | ILogoutStartSuccessful
    | TWizardStorageClearStepsAction
    ;
